<header class="relative px-4 pb-4 pt-2">
  <h1 class="text-2xl font-semibold text-greyscale-900">Mailing List</h1>
  <p class="text-sm text-greyscale-600">
    Export filtered user emails for marketing purposes
  </p>
</header>

<main class="p-4">
  <!-- Filters Section -->
  <div class="mb-6 rounded-lg border border-greyscale-200 bg-white p-4">
    <h2 class="mb-4 text-lg font-medium text-greyscale-800">Filters</h2>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      <!-- Languages -->
      <div>
        <label for="languages" class="mb-1 block text-sm font-medium">Languages</label>
        <p-multiselect
          inputId="languages"
          [options]="languageOptions"
          [(ngModel)]="selectedLanguages"
          optionLabel="label"
          optionValue="value"
          placeholder="All languages"
          [style]="{ width: '100%' }"
        />
      </div>

      <!-- Packs -->
      <div>
        <label for="packs" class="mb-1 block text-sm font-medium">Packs</label>
        <p-multiselect
          inputId="packs"
          [options]="packOptions()"
          [(ngModel)]="selectedPacks"
          optionLabel="label"
          optionValue="value"
          placeholder="All packs"
          [style]="{ width: '100%' }"
        />
      </div>

      <!-- Registration Date From -->
      <div>
        <label for="registeredAfter" class="mb-1 block text-sm font-medium">Registered After</label>
        <p-datepicker
          inputId="registeredAfter"
          [(ngModel)]="registeredAfter"
          dateFormat="dd/mm/yy"
          [showIcon]="true"
          [showClear]="true"
          placeholder="Select date..."
          [style]="{ width: '100%' }"
        />
      </div>

      <!-- Registration Date To -->
      <div>
        <label for="registeredBefore" class="mb-1 block text-sm font-medium">Registered Before</label>
        <p-datepicker
          inputId="registeredBefore"
          [(ngModel)]="registeredBefore"
          dateFormat="dd/mm/yy"
          [showIcon]="true"
          [showClear]="true"
          placeholder="Select date..."
          [style]="{ width: '100%' }"
        />
      </div>

      <!-- Last Login From -->
      <div>
        <label for="lastLoginAfter" class="mb-1 block text-sm font-medium">Last Login After</label>
        <p-datepicker
          inputId="lastLoginAfter"
          [(ngModel)]="lastLoginAfter"
          dateFormat="dd/mm/yy"
          [showIcon]="true"
          [showClear]="true"
          placeholder="Select date..."
          [style]="{ width: '100%' }"
        />
      </div>

      <!-- Last Login To -->
      <div>
        <label for="lastLoginBefore" class="mb-1 block text-sm font-medium">Last Login Before</label>
        <p-datepicker
          inputId="lastLoginBefore"
          [(ngModel)]="lastLoginBefore"
          dateFormat="dd/mm/yy"
          [showIcon]="true"
          [showClear]="true"
          placeholder="Select date..."
          [style]="{ width: '100%' }"
        />
      </div>

      <!-- Include Opted Out -->
      <div class="flex items-center gap-2 pt-6">
        <p-toggleSwitch inputId="includeOptedOut" [(ngModel)]="includeOptedOut" />
        <label for="includeOptedOut" class="text-sm">Include opted-out</label>
      </div>

      <!-- Admins Only -->
      <div class="flex items-center gap-2 pt-6">
        <p-toggleSwitch inputId="adminsOnly" [(ngModel)]="adminsOnly" />
        <label for="adminsOnly" class="text-sm">Admins only</label>
      </div>
    </div>

    <div class="mt-4 flex gap-2">
      <p-button
        label="Apply Filters"
        icon="pi pi-filter"
        (onClick)="loadMailingList()"
        [loading]="loading"
      />
      <p-button
        label="Reset"
        icon="pi pi-filter-slash"
        (onClick)="resetFilters()"
        [outlined]="true"
      />
    </div>
  </div>

  <!-- Results Section -->
  @if (entries.length > 0) {
    <div class="mb-6 rounded-lg border border-greyscale-200 bg-white p-4">
      <div class="mb-4 flex items-center justify-between">
        <h2 class="text-lg font-medium text-greyscale-800">
          Results ({{ entries.length }} users)
        </h2>
        <div class="flex gap-2">
          <p-button
            label="Select All"
            (onClick)="selectAll()"
            [text]="true"
            size="small"
          />
          <p-button
            label="Deselect All"
            (onClick)="deselectAll()"
            [text]="true"
            size="small"
          />
        </div>
      </div>

      <p-table
        [value]="entries"
        [(selection)]="selectedEntries"
        (selectionChange)="onSelectionChange()"
        dataKey="email"
        [paginator]="entries.length > 20"
        [rows]="20"
        styleClass="p-datatable-sm"
      >
        <ng-template #header>
          <tr>
            <th style="width: 3rem">
              <p-tableHeaderCheckbox />
            </th>
            <th>Email</th>
            <th>Name</th>
            <th>Company</th>
            <th>Language</th>
            <th>Pack</th>
            <th>Last Login</th>
            <th>Subscribed</th>
          </tr>
        </ng-template>

        <ng-template #body let-entry>
          <tr>
            <td>
              <p-tableCheckbox [value]="entry" />
            </td>
            <td class="font-medium">{{ entry.email }}</td>
            <td>{{ entry.firstName }} {{ entry.lastName }}</td>
            <td>{{ entry.companyName }}</td>
            <td>
              <span
                class="rounded bg-greyscale-100 px-2 py-1 text-xs font-medium"
              >
                {{ getLanguageLabel(entry.preferredLanguage) }}
              </span>
            </td>
            <td>
              <p-tag
                [value]="entry.pack"
                severity="secondary"
              />
            </td>
            <td>{{ formatDate(entry.lastLogin) }}</td>
            <td>
              @if (entry.marketingOptOut) {
                <i class="pi pi-times-circle text-error-500"></i>
              } @else {
                <i class="pi pi-check-circle text-validation-500"></i>
              }
            </td>
          </tr>
        </ng-template>

        <ng-template #emptymessage>
          <tr>
            <td colspan="8" class="py-12 text-center">
              <i class="pi pi-inbox mb-4 text-4xl text-gray-400"></i>
              <p class="text-gray-500">No users found with current filters</p>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  }

  <!-- Export Section - Always visible -->
  <div class="rounded-lg border border-greyscale-200 bg-white p-4">
    <h2 class="mb-4 text-lg font-medium text-greyscale-800">
      Export
      @if (selectedEntries.length > 0) {
        <span class="text-greyscale-500">({{ selectedEntries.length }} selected)</span>
      }
    </h2>

    @if (selectedEntries.length > 0) {
      <fieldset class="mb-4 border-0 p-0">
        <legend class="mb-2 block text-sm font-medium">Format</legend>
        <div class="flex gap-4">
          <div class="flex items-center gap-2">
            <p-radioButton
              name="format"
              value="semicolon"
              [(ngModel)]="exportFormat"
              (ngModelChange)="onExportFormatChange()"
              inputId="semicolon"
            />
            <label for="semicolon" class="text-sm">Semicolon (;)</label>
          </div>
          <div class="flex items-center gap-2">
            <p-radioButton
              name="format"
              value="newline"
              [(ngModel)]="exportFormat"
              (ngModelChange)="onExportFormatChange()"
              inputId="newline"
            />
            <label for="newline" class="text-sm">One per line</label>
          </div>
          <div class="flex items-center gap-2">
            <p-radioButton
              name="format"
              value="comma"
              [(ngModel)]="exportFormat"
              (ngModelChange)="onExportFormatChange()"
              inputId="comma"
            />
            <label for="comma" class="text-sm">Comma (,)</label>
          </div>
        </div>
      </fieldset>

      <textarea
        pInputTextarea
        [ngModel]="exportedEmails"
        [rows]="5"
        [readonly]="true"
        class="mb-4 w-full font-mono text-sm"
      ></textarea>

      <p-button
        label="Copy to Clipboard"
        icon="pi pi-copy"
        (onClick)="copyToClipboard()"
      />
    } @else {
      <div class="py-8 text-center">
        <i class="pi pi-upload mb-2 text-3xl text-greyscale-300"></i>
        <p class="text-greyscale-500">Select users above to export emails</p>
      </div>
    }
  </div>
</main>

<p-toast />
